import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Palette, Building2, Key } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Palette: Palette,
  Building2: Building2,
  Key: Key,
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const processSteps = [
    {
      number: '01',
      title: 'Discovery',
      description: 'We begin by understanding your vision, needs, and the unique context of your project.'
    },
    {
      number: '02',
      title: 'Concept',
      description: 'Our team develops initial concepts that respond to your brief and site conditions.'
    },
    {
      number: '03',
      title: 'Design Development',
      description: 'Selected concepts are refined into comprehensive design documents.'
    },
    {
      number: '04',
      title: 'Realization',
      description: 'We guide your project through construction with meticulous attention to detail.'
    }
  ];

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="services-hero">
        <div className="max-w-4xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-dm-sans text-xs text-stone-400 tracking-wide uppercase mb-4 block"
          >
            Services
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-stone-900 leading-tight mb-8"
          >
            Comprehensive
            <br />
            <span className="italic">design solutions</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-dm-sans text-lg text-stone-500 leading-relaxed max-w-2xl"
          >
            From initial concept to final construction, we offer a full spectrum of architectural services tailored to bring your vision to life with precision and creativity.
          </motion.p>
        </div>
      </section>

      {/* Services Section - New Design */}
      <section className="py-24 md:py-32 bg-stone-50" data-testid="services-grid">
        <div className="px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="space-y-24">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="grid grid-cols-1 lg:grid-cols-2 gap-12 animate-pulse">
                  <div className="aspect-[4/3] bg-stone-200" />
                  <div className="space-y-4">
                    <div className="h-8 bg-stone-200 w-1/2" />
                    <div className="h-4 bg-stone-200 w-full" />
                    <div className="h-4 bg-stone-200 w-5/6" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-32">
              {services.map((service, index) => {
                const IconComponent = iconMap[service.icon] || Palette;
                const isEven = index % 2 === 0;
                
                return (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 60 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                    className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                      isEven ? '' : 'lg:direction-rtl'
                    }`}
                    data-testid={`service-card-${service.id}`}
                  >
                    {/* Image */}
                    <motion.div
                      className={`relative overflow-hidden ${isEven ? '' : 'lg:order-2'}`}
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <motion.div
                        className="aspect-[4/3] overflow-hidden bg-stone-200"
                        whileHover="hover"
                      >
                        <motion.img
                          src={service.image_url}
                          alt={service.title}
                          className="w-full h-full object-cover"
                          initial={{ scale: 1 }}
                          whileHover={{ scale: 1.08 }}
                          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        />
                      </motion.div>
                      {/* Decorative corner */}
                      <div className={`absolute -bottom-4 ${isEven ? '-right-4' : '-left-4'} w-24 h-24 border border-stone-300`} />
                    </motion.div>

                    {/* Content */}
                    <motion.div
                      className={`${isEven ? '' : 'lg:order-1'}`}
                      initial={{ opacity: 0, x: isEven ? 30 : -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <motion.div 
                        className="w-14 h-14 rounded-full border border-stone-300 flex items-center justify-center mb-8 group"
                        whileHover={{ 
                          backgroundColor: '#1c1917',
                          borderColor: '#1c1917',
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <IconComponent 
                          size={24} 
                          className="text-stone-600 group-hover:text-white transition-colors duration-300" 
                        />
                      </motion.div>
                      
                      <span className="font-dm-sans text-xs text-stone-400 tracking-wide uppercase mb-4 block">
                        0{index + 1}
                      </span>
                      
                      <h3 className="font-manrope text-3xl md:text-4xl font-light tracking-tight text-stone-900 mb-6">
                        {service.title}
                      </h3>
                      
                      <p className="font-dm-sans text-stone-500 leading-relaxed text-lg">
                        {service.description}
                      </p>
                      
                      <motion.div
                        className="mt-8"
                        whileHover={{ x: 5 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Link
                          to="/contact"
                          className="inline-flex items-center gap-2 font-manrope text-xs uppercase tracking-[0.2em] font-medium text-stone-900 hover:text-stone-500 transition-colors duration-300"
                        >
                          Inquire About This Service
                          <ArrowRight size={14} />
                        </Link>
                      </motion.div>
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24" data-testid="process-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16 md:mb-24"
        >
          <span className="font-dm-sans text-xs text-stone-400 tracking-wide uppercase mb-4 block">
            Our Process
          </span>
          <h2 className="font-manrope text-3xl md:text-4xl font-light tracking-tight text-stone-900">
            How we work
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {processSteps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.15 }}
              className="relative group"
              data-testid={`process-step-${index}`}
              whileHover={{ y: -5 }}
            >
              <motion.span 
                className="font-manrope text-6xl font-extralight text-stone-200 mb-4 block group-hover:text-stone-300 transition-colors duration-300"
              >
                {step.number}
              </motion.span>
              <h3 className="font-manrope text-xl font-light tracking-tight text-stone-900 mb-3">
                {step.title}
              </h3>
              <p className="font-dm-sans text-sm text-stone-500 leading-relaxed">
                {step.description}
              </p>
              {index < processSteps.length - 1 && (
                <div className="hidden lg:block absolute top-8 right-0 w-1/2 h-[1px] bg-stone-200" />
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 bg-stone-100">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto"
        >
          <h2 className="font-manrope text-3xl md:text-4xl font-light tracking-tight text-stone-900 mb-6">
            Ready to start your project?
          </h2>
          <p className="font-dm-sans text-stone-500 leading-relaxed mb-10">
            Let's discuss how our services can help bring your architectural vision to life.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta"
            className="inline-flex items-center gap-3 rounded-full px-10 py-4 text-xs uppercase tracking-widest font-manrope font-medium bg-stone-900 text-white hover:bg-stone-800 transition-colors duration-300"
          >
            Get Started
            <ArrowRight size={16} />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
